import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { SingAlongService, SingAlongBundle, SingAlongPurchase } from '../services/SingAlongService';
import { useSingAlongBasePath } from '../hooks/useSingAlongBasePath';
import { trackPurchase } from '../lib/analytics';
import { TvFocusableInput } from '../components/TvFocusableInput';
import { Loader as Loader2, Music, Check, CircleAlert as AlertCircle, ChevronDown, ChevronUp } from 'lucide-react';

export const SingAlongSetup: React.FC = () => {
  const navigate = useNavigate();
  const basePath = useSingAlongBasePath();
  const [searchParams] = useSearchParams();
  const [purchase, setPurchase] = useState<SingAlongPurchase | null>(null);
  const [bundles, setBundles] = useState<SingAlongBundle[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [kioskName, setKioskName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugAvailable, setSlugAvailable] = useState<boolean | null>(null);
  const [slugChecking, setSlugChecking] = useState(false);
  const [passcode, setPasscode] = useState('');
  const [selectedBundles, setSelectedBundles] = useState<Set<string>>(new Set());
  const [expandedBundle, setExpandedBundle] = useState<string | null>(null);
  const [purchaseTracked, setPurchaseTracked] = useState(false);

  useEffect(() => {
    if (purchase && searchParams.get('success') === 'true' && !purchaseTracked) {
      setPurchaseTracked(true);
      const passName = purchase.pass?.name || 'Sing-Along Pass';
      const priceCents = purchase.pass?.price_cents || 0;
      trackPurchase(passName, priceCents, 'USD', purchase.id);
    }
  }, [purchase, searchParams, purchaseTracked]);

  useEffect(() => {
    (async () => {
      const user = await SingAlongService.getCurrentUser();
      if (!user) { navigate(`/${basePath}/signup`, { replace: true }); return; }

      try {
        const activePurchase = await SingAlongService.getActivePurchase();
        if (!activePurchase) { navigate(`/${basePath}/plans`, { replace: true }); return; }
        if (activePurchase.league_id) { navigate(`/${basePath}/dashboard`, { replace: true }); return; }

        setPurchase(activePurchase);
        const bundleData = await SingAlongService.getBundles();
        setBundles(bundleData);

        if (activePurchase.pass?.bundle_limit === null) {
          setSelectedBundles(new Set(bundleData.map(b => b.id)));
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load');
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate, basePath]);

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .slice(0, 50);
  };

  useEffect(() => {
    if (!kioskName) { setSlug(''); setSlugAvailable(null); return; }
    const newSlug = generateSlug(kioskName);
    setSlug(newSlug);

    if (newSlug.length < 3) { setSlugAvailable(null); return; }

    setSlugChecking(true);
    const timer = setTimeout(async () => {
      try {
        const available = await SingAlongService.checkSlugAvailable(newSlug);
        setSlugAvailable(available);
      } catch {
        setSlugAvailable(null);
      } finally {
        setSlugChecking(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [kioskName]);

  const toggleBundle = (bundleId: string) => {
    if (purchase?.pass?.bundle_limit === null) return;
    const next = new Set(selectedBundles);
    if (next.has(bundleId)) {
      next.delete(bundleId);
    } else {
      if (purchase?.pass?.bundle_limit && next.size >= purchase.pass.bundle_limit) return;
      next.add(bundleId);
    }
    setSelectedBundles(next);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!purchase || !slug || slugAvailable !== true || !passcode || selectedBundles.size === 0) return;

    setSubmitting(true);
    setError(null);
    try {
      await SingAlongService.createKiosk({
        purchaseId: purchase.id,
        name: kioskName,
        slug,
        passcode,
        bundleIds: [...selectedBundles],
      });
      navigate(`/${basePath}/dashboard`, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create kiosk');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-orange-400" />
      </div>
    );
  }

  const bundleLimit = purchase?.pass?.bundle_limit;
  const isUnlimited = bundleLimit === null;

  return (
    <div className="min-h-screen bg-slate-950 text-white px-4 py-16">
      <div className="absolute inset-0 bg-gradient-to-br from-orange-600/10 via-transparent to-blue-600/10 pointer-events-none" />

      <div className="relative max-w-2xl mx-auto">
        <div className="text-center mb-10">
          <h1 className="text-3xl sm:text-4xl font-black mb-3">Set Up Your Sing-Along</h1>
          <p className="text-white/50">Name your kiosk, set a passcode, and pick your songs</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Kiosk Name */}
          <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-6 space-y-4">
            <h2 className="font-bold text-lg">Name Your Kiosk</h2>
            <div>
              <TvFocusableInput
                value={kioskName}
                onChange={(e) => setKioskName(e.target.value)}
                placeholder="e.g. Smith Family Sing"
                maxLength={60}
                required
                enterKeyHint="next"
                className="w-full px-4 py-3 bg-white/5 border border-white/15 rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-orange-500/50 transition"
              />
              {slug && (
                <div className="mt-2 flex items-center gap-2 text-sm">
                  <span className="text-white/40">URL: /league/{slug}</span>
                  {slugChecking && <Loader2 size={14} className="animate-spin text-white/40" />}
                  {!slugChecking && slugAvailable === true && <Check size={14} className="text-green-400" />}
                  {!slugChecking && slugAvailable === false && (
                    <span className="text-red-400 text-xs">Already taken</span>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Passcode */}
          <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-6 space-y-4">
            <h2 className="font-bold text-lg">Set a Passcode</h2>
            <p className="text-sm text-white/50">Participants will enter this to join your sing-along</p>
            <TvFocusableInput
              value={passcode}
              onChange={(e) => setPasscode(e.target.value.replace(/[^a-zA-Z0-9]/g, '').slice(0, 8))}
              placeholder="e.g. SING2024"
              minLength={4}
              maxLength={8}
              required
              enterKeyHint="done"
              className="w-full px-4 py-3 bg-white/5 border border-white/15 rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-orange-500/50 transition uppercase tracking-widest font-mono text-lg"
            />
            <p className="text-xs text-white/30">4-8 alphanumeric characters</p>
          </div>

          {/* Bundle Selection */}
          <div className="bg-white/[0.03] border border-white/10 rounded-2xl p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-bold text-lg">
                {isUnlimited ? 'Your Song Bundles (Full Catalog)' : 'Choose Song Bundles'}
              </h2>
              {!isUnlimited && (
                <span className="text-sm text-orange-400 font-medium">
                  {selectedBundles.size}/{bundleLimit} selected
                </span>
              )}
            </div>

            <div className="space-y-3">
              {bundles.map((bundle) => {
                const isSelected = selectedBundles.has(bundle.id);
                const isExpanded = expandedBundle === bundle.id;
                const canSelect = isUnlimited || isSelected || selectedBundles.size < (bundleLimit || 0);

                return (
                  <div key={bundle.id} className="border border-white/10 rounded-xl overflow-hidden">
                    <div
                      className={`flex items-center gap-3 p-4 cursor-pointer transition ${
                        isSelected ? 'bg-orange-500/10 border-orange-500/20' : 'hover:bg-white/[0.02]'
                      } ${!canSelect && !isSelected ? 'opacity-50' : ''}`}
                      onClick={() => !isUnlimited && toggleBundle(bundle.id)}
                    >
                      <div className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 transition ${
                        isSelected ? 'bg-orange-500 border-orange-500' : 'border-white/30'
                      }`}>
                        {isSelected && <Check size={12} className="text-white" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold truncate">{bundle.name}</div>
                        <div className="text-xs text-white/40">{bundle.song_count} songs</div>
                      </div>
                      <button
                        type="button"
                        onClick={(ev) => { ev.stopPropagation(); setExpandedBundle(isExpanded ? null : bundle.id); }}
                        className="p-1 text-white/40 hover:text-white/70 transition"
                      >
                        {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                      </button>
                    </div>
                    {isExpanded && bundle.songs && bundle.songs.length > 0 && (
                      <div className="border-t border-white/5 px-4 py-3 bg-black/20 space-y-1.5 max-h-48 overflow-y-auto">
                        {bundle.songs.map((song) => (
                          <div key={song.id} className="flex items-center gap-2 text-sm">
                            <Music size={12} className="text-white/30 shrink-0" />
                            <span className="text-white/70 truncate">{song.title}</span>
                            <span className="text-white/30 truncate">{song.artist}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {bundles.length === 0 && (
              <p className="text-white/40 text-center py-4">No song bundles available yet.</p>
            )}
          </div>

          {error && (
            <div className="flex items-start gap-2 p-4 rounded-xl bg-red-500/10 border border-red-500/20">
              <AlertCircle size={18} className="text-red-400 shrink-0 mt-0.5" />
              <p className="text-sm text-red-300">{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={submitting || !kioskName || slugAvailable !== true || !passcode || passcode.length < 4 || selectedBundles.size === 0}
            className="w-full py-4 bg-gradient-to-r from-orange-500 to-amber-500 text-white font-bold text-lg rounded-xl hover:shadow-lg hover:shadow-orange-500/25 transition disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {submitting ? (
              <Loader2 size={20} className="animate-spin" />
            ) : (
              'Launch Your Sing-Along'
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default SingAlongSetup;
